import { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendSubscribeWebhook } from '../../utils/webhookUtils';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const [response] = await Promise.allSettled([
        fetch('https://readdy.ai/api/form/d6t4n6vcrcujsk0v13d0', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ email, source: 'Footer Signup' }).toString(),
        }),
        sendSubscribeWebhook({
          email,
          source: 'Footer Signup',
          page_url: window.location.href,
        }),
      ]);

      const localOk = response.status === 'fulfilled' && (response.value as Response).ok;
      if (localOk) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <footer className="bg-black/40 backdrop-blur-sm" style={{ borderTop: '2px solid transparent', borderImage: 'linear-gradient(to right, #10b981, #a855f7, #06b6d4) 1' }}>
      {/* Newsletter Section */}
      <div className="bg-gradient-to-r from-purple-900/15 via-cyan-900/15 to-emerald-900/15 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-3 space-y-6">
              <h3 className="text-4xl font-bold text-white uppercase tracking-tight">
                Stay Updated on the Best Tech
              </h3>
              <p className="text-lg text-gray-300 leading-relaxed">
                Get new reviews, product recommendations, and tech buying guides delivered to your inbox.
              </p>
            </div>

            {/* Right Form */}
            <div className="lg:col-span-2">
              <form id="newsletter-form" onSubmit={handleSubmit} className="space-y-4">
                <input type="hidden" name="source" value="footer" />
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="w-full px-6 py-4 bg-black border-2 border-gray-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500 focus:shadow-lg focus:shadow-cyan-500/20 transition-all duration-200 pr-16"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-gradient-to-r from-purple-600 to-cyan-600 rounded-lg text-white hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-200 disabled:opacity-50 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-send-plane-fill"></i>
                    )}
                  </button>
                </div>
                {submitStatus === 'success' && (
                  <p className="text-emerald-400 text-sm">Thanks for subscribing. Check your email.</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>
                )}
                <p className="text-xs text-gray-400 font-light tracking-wide" style={{ fontFamily: "'Lato', sans-serif" }}>
                  No spam. Unsubscribe anytime. We respect your privacy.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Affiliate Disclosure & Copyright Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-8">

          {/* Main bottom row: 3 centered columns */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-8 sm:gap-16 mb-6">

            {/* Logo & Tagline */}
            <div className="flex flex-col items-center gap-2">
              <Link to="/" className="flex items-center gap-3 cursor-pointer group">
                <div className="relative w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0">
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-cyan-500/40 to-purple-600/40 blur-xl group-hover:blur-2xl transition-all duration-500 scale-110"></div>
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-cyan-400/20 to-purple-500/20 blur-md group-hover:from-cyan-400/30 group-hover:to-purple-500/30 transition-all duration-300"></div>
                  <div
                    className="relative w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl overflow-hidden transition-all duration-300 border border-white/10 group-hover:border-cyan-400/40 shadow-[0_0_0_1px_rgba(6,182,212,0.15),0_0_16px_rgba(6,182,212,0.25),0_0_32px_rgba(147,51,234,0.15)] group-hover:shadow-[0_0_0_1px_rgba(6,182,212,0.3),0_0_20px_rgba(6,182,212,0.4),0_0_40px_rgba(147,51,234,0.25)]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(6,182,212,0.12) 0%, rgba(0,0,0,0.7) 50%, rgba(147,51,234,0.12) 100%)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div className="absolute inset-0 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 60%)' }}></div>
                    <div className="absolute inset-px rounded-[10px] border border-white/5"></div>
                    <i className="ri-cpu-line text-sm sm:text-base relative z-10"
                      style={{
                        background: 'linear-gradient(135deg, #22d3ee, #a855f7)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        filter: 'drop-shadow(0 0 6px rgba(6,182,212,0.6))',
                      }}
                    ></i>
                  </div>
                </div>
                <div className="flex flex-col justify-center gap-0.5">
                  <div className="flex items-baseline gap-0.5">
                    <span
                      className="text-sm sm:text-base font-black tracking-tight text-emerald-400 group-hover:text-emerald-300 transition-colors duration-200"
                      style={{ textShadow: '0 0 12px rgba(52,211,153,0.6)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em' }}
                    >
                      WORK
                    </span>
                    <span className="text-emerald-500/40 text-xs font-light mx-0.5">·</span>
                    <span
                      className="text-sm sm:text-base font-black tracking-tight text-purple-400 group-hover:text-purple-300 transition-colors duration-200"
                      style={{ textShadow: '0 0 12px rgba(192,132,252,0.6)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em' }}
                    >
                      PLAY
                    </span>
                    <span className="text-cyan-500/40 text-xs font-light mx-0.5">·</span>
                    <span
                      className="text-sm sm:text-base font-black tracking-tight text-cyan-400 group-hover:text-cyan-300 transition-colors duration-200"
                      style={{ textShadow: '0 0 12px rgba(34,211,238,0.6)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em' }}
                    >
                      SAVE
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <div className="h-px flex-1 bg-gradient-to-r from-emerald-500/60 via-purple-500/60 to-cyan-500/60"></div>
                    <span
                      className="text-[8px] uppercase tracking-[0.2em] font-black"
                      style={{
                        fontFamily: "'Barlow Condensed', sans-serif",
                        color: '#c084fc',
                        textShadow: '0 0 8px rgba(192,132,252,0.9), 0 0 16px rgba(192,132,252,0.5)',
                        letterSpacing: '0.22em',
                      }}
                    >Tech Reviews</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-cyan-500/60 via-purple-500/60 to-emerald-500/60"></div>
                  </div>
                </div>
              </Link>

              <p
                className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-center"
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  letterSpacing: '0.18em',
                  background: 'linear-gradient(90deg, #34d399, #c084fc, #22d3ee)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 6px rgba(192,132,252,0.5))',
                }}
              >
                Review. Compare. Save.
              </p>
            </div>

            {/* Explore & Resources columns */}
            <div className="flex gap-8 sm:gap-16 justify-center">
              {/* Explore */}
              <div className="flex flex-col items-center text-center">
                <h4
                  className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-300 mb-3 font-bold"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.2em' }}
                >
                  Explore
                </h4>
                <div className="flex flex-col gap-1.5">
                  {[
                    { name: 'Gaming Tech', link: '/gaming' },
                    { name: 'Streaming Gear', link: '/streaming' },
                    { name: 'Office Setup', link: '/office' },
                    { name: 'Deal Alerts', link: '/articles' },
                    { name: 'About Us', link: '/about' },
                  ].map((item, index) => (
                    <Link
                      key={index}
                      to={item.link}
                      className="text-[10px] sm:text-xs font-semibold text-purple-400 hover:text-purple-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.05em' }}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Resources */}
              <div className="flex flex-col items-center text-center">
                <h4
                  className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-300 mb-3 font-bold"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.2em' }}
                >
                  Resources
                </h4>
                <div className="flex flex-col gap-1.5">
                  {[
                    { name: 'Review Policy', link: '/review-policy' },
                    { name: 'Affiliate Disclosure', link: '/affiliate-disclosure' },
                    { name: 'Privacy Policy', link: '/privacy-policy' },
                    { name: 'Contact', link: '/about' },
                  ].map((item, index) => (
                    <Link
                      key={index}
                      to={item.link}
                      className="text-[10px] sm:text-xs font-semibold text-emerald-400 hover:text-emerald-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.05em' }}
                    >
                      {item.name}
                    </Link>
                  ))}
                  <a
                    href="/sitemap.xml"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] sm:text-xs font-semibold text-emerald-400 hover:text-emerald-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.05em' }}
                  >
                    Sitemap
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Affiliate disclosure + copyright */}
          <div className="border-t border-gray-800 pt-5 space-y-2">
            <p className="text-center text-xs text-gray-300 leading-relaxed max-w-2xl mx-auto">
              Work Play Save may earn commissions from qualifying purchases through affiliate links. This helps support the site at no extra cost to you.
            </p>
            <p className="text-center text-xs text-gray-300">
              © 2026 Work Play Save. All rights reserved.
            </p>
          </div>

        </div>
      </div>
    </footer>
  );
}